const BACKUP_SCHEMA_VERSION = 1;

const STORAGE_KEYS = {
  currentResults: 'founderDnaResults',
  resultsHistory: 'founderDna_results_history_v1',
  checkIns: 'founderDna_checkIns_v1',
  emailCaptures: 'founderDna_emailCaptures_v1',
};

const safeParseJson = (value) => {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch (e) {
    return null;
  }
};

const safeStringifyJson = (value) => {
  try {
    return JSON.stringify(value);
  } catch (e) {
    return null;
  }
};

const isIsoDateString = (value) => {
  if (!value || typeof value !== 'string') return false;
  const t = Date.parse(value);
  return Number.isFinite(t);
};

const makeId = () => `${Date.now()}_${Math.random().toString(16).slice(2, 10)}`;

const dedupeByKey = (items, getKey) => {
  const out = [];
  const seen = new Set();
  items.forEach((item) => {
    const key = getKey(item);
    if (!key) return;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(item);
  });
  return out;
};

const normalizeCurrentResults = (raw) => {
  const parsed = typeof raw === 'string' ? safeParseJson(raw) : raw;
  if (!parsed) return null;

  // Backward compatibility (v1 stored the profile object directly)
  if (parsed && parsed.primaryType) {
    return { profile: parsed, answers: [], createdAt: null, schemaVersion: 1 };
  }

  if (parsed && parsed.profile) {
    return {
      profile: parsed.profile,
      answers: Array.isArray(parsed.answers) ? parsed.answers : [],
      createdAt: parsed.createdAt || null,
      schemaVersion: parsed.schemaVersion || 2,
    };
  }

  return null;
};

const normalizeResultsHistory = (maybeArray) => {
  if (!Array.isArray(maybeArray)) return [];
  return maybeArray
    .filter((r) => r && typeof r === 'object' && r.profile && r.createdAt)
    .map((r) => ({
      id: r.id || makeId(),
      name: typeof r.name === 'string' ? r.name : 'Untitled run',
      createdAt: r.createdAt,
      profile: r.profile,
      answers: Array.isArray(r.answers) ? r.answers : [],
      schemaVersion: r.schemaVersion || 3,
    }))
    .filter((r) => isIsoDateString(r.createdAt))
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
};

const normalizeCheckIns = (maybeArray) => {
  if (!Array.isArray(maybeArray)) return [];
  return maybeArray
    .filter((c) => c && typeof c === 'object' && c.createdAt)
    .filter((c) => isIsoDateString(c.createdAt))
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
};

const normalizeEmailCaptures = (maybeArray) => {
  if (!Array.isArray(maybeArray)) return [];
  return maybeArray
    .filter((e) => e && typeof e === 'object' && typeof e.email === 'string' && e.createdAt)
    .filter((e) => isIsoDateString(e.createdAt))
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
};

export const buildPrivateBackup = () => {
  const currentResultsRaw = localStorage.getItem(STORAGE_KEYS.currentResults);
  const resultsHistoryRaw = localStorage.getItem(STORAGE_KEYS.resultsHistory);
  const checkInsRaw = localStorage.getItem(STORAGE_KEYS.checkIns);
  const emailCapturesRaw = localStorage.getItem(STORAGE_KEYS.emailCaptures);

  const currentResults = normalizeCurrentResults(currentResultsRaw);
  const resultsHistory = normalizeResultsHistory(safeParseJson(resultsHistoryRaw));
  const checkIns = normalizeCheckIns(safeParseJson(checkInsRaw));
  const emailCaptures = normalizeEmailCaptures(safeParseJson(emailCapturesRaw));

  return {
    schemaVersion: BACKUP_SCHEMA_VERSION,
    exportedAt: new Date().toISOString(),
    data: {
      currentResults,
      resultsHistory,
      checkIns,
      emailCaptures,
    },
  };
};

export const getBackupSummary = (backup) => {
  const d = backup?.data || {};
  return {
    hasCurrentResults: Boolean(d.currentResults && d.currentResults.profile && d.currentResults.profile.primaryType),
    resultsHistoryCount: Array.isArray(d.resultsHistory) ? d.resultsHistory.length : 0,
    checkInsCount: Array.isArray(d.checkIns) ? d.checkIns.length : 0,
    emailCapturesCount: Array.isArray(d.emailCaptures) ? d.emailCaptures.length : 0,
  };
};

export const downloadPrivateBackup = (backup) => {
  const json = safeStringifyJson(backup);
  if (!json) throw new Error('Failed to serialize backup');

  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const stamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
  a.href = url;
  a.download = `founderdna-backup-${stamp}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export const parsePrivateBackup = (jsonText) => {
  const parsed = safeParseJson(jsonText);
  if (!parsed || typeof parsed !== 'object') {
    throw new Error('Invalid JSON backup');
  }
  if (parsed.schemaVersion !== BACKUP_SCHEMA_VERSION) {
    throw new Error('Unsupported backup version');
  }
  if (!parsed.exportedAt || !isIsoDateString(parsed.exportedAt)) {
    throw new Error('Backup is missing exportedAt');
  }

  const data = parsed.data || {};

  return {
    schemaVersion: BACKUP_SCHEMA_VERSION,
    exportedAt: parsed.exportedAt,
    data: {
      currentResults: normalizeCurrentResults(data.currentResults) || null,
      resultsHistory: normalizeResultsHistory(data.resultsHistory),
      checkIns: normalizeCheckIns(data.checkIns),
      emailCaptures: normalizeEmailCaptures(data.emailCaptures),
    },
  };
};

export const restorePrivateBackup = (backup, { mode = 'merge' } = {}) => {
  if (!backup || backup.schemaVersion !== BACKUP_SCHEMA_VERSION) {
    throw new Error('Unsupported backup version');
  }
  const incoming = backup.data || {};

  const existing = buildPrivateBackup().data;

  const mergedHistory = mode === 'replace'
    ? normalizeResultsHistory(incoming.resultsHistory)
    : normalizeResultsHistory(dedupeByKey(
      [...normalizeResultsHistory(incoming.resultsHistory), ...normalizeResultsHistory(existing.resultsHistory)],
      (r) => `${r.createdAt}_${String(r?.profile?.primaryType || '').toLowerCase()}`
    ));

  const mergedCheckIns = mode === 'replace'
    ? normalizeCheckIns(incoming.checkIns)
    : normalizeCheckIns(dedupeByKey(
      [...normalizeCheckIns(incoming.checkIns), ...normalizeCheckIns(existing.checkIns)],
      (c) => String(c.createdAt || '')
    ));

  const mergedEmails = mode === 'replace'
    ? normalizeEmailCaptures(incoming.emailCaptures)
    : normalizeEmailCaptures(dedupeByKey(
      [...normalizeEmailCaptures(incoming.emailCaptures), ...normalizeEmailCaptures(existing.emailCaptures)],
      (e) => `${String(e.email || '').toLowerCase()}_${String(e.createdAt || '')}`
    ));

  const nextResultsHistory = mergedHistory.slice(0, 30);
  const nextCheckIns = mergedCheckIns.slice(0, 50);
  const nextEmails = mergedEmails.slice(0, 50);

  const currentResults = normalizeCurrentResults(incoming.currentResults)
    || (mode === 'replace' ? null : normalizeCurrentResults(existing.currentResults));

  // Write back
  localStorage.setItem(STORAGE_KEYS.resultsHistory, JSON.stringify(nextResultsHistory));
  localStorage.setItem(STORAGE_KEYS.checkIns, JSON.stringify(nextCheckIns));
  localStorage.setItem(STORAGE_KEYS.emailCaptures, JSON.stringify(nextEmails));
  try {
    localStorage.removeItem('founderDna_analyticsEvents_v1');
  } catch (e) {
    // Legacy analytics storage cleanup
  }

  if (currentResults) {
    localStorage.setItem(STORAGE_KEYS.currentResults, JSON.stringify(currentResults));
  } else if (mode === 'replace') {
    localStorage.removeItem(STORAGE_KEYS.currentResults);
  }

  return {
    restoredAt: new Date().toISOString(),
    summary: getBackupSummary({
      data: {
        currentResults,
        resultsHistory: nextResultsHistory,
        checkIns: nextCheckIns,
        emailCaptures: nextEmails,
      },
    }),
  };
};

const EXTRA_STORAGE_KEYS = [
  'founderDna_revealSeen',
  'founderDna_analyticsEvents_v1',
  'founderDna_consent_v1',
  'founderDna_payment',
];

/** Remove all FounderDNA data stored in this browser (GDPR-style local erasure). */
export const clearAllLocalUserData = () => {
  Object.values(STORAGE_KEYS).forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      // Non blocking
    }
  });
  EXTRA_STORAGE_KEYS.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      // Non blocking
    }
  });
  try {
    sessionStorage.removeItem('founderDna_revealSeen');
  } catch (e) {
    // Non blocking
  }
};

